---
title: Coastal Monolith
year: 2025
typology: residential
location: Cocoa Beach, FL
summary: A 4,200-sq-ft single-family residence where the architecture and interior were composed as one continuous gesture — limewash plaster, weathered bronze, and a single, structural fireplace anchoring the great room.
heroImage: /projects/coastal-monolith/hero.svg
heroAlt: View of the Coastal Monolith great room with a bronze-clad fireplace anchoring a long limewash-plaster wall.
materials:
  - Limewash plaster
  - Weathered bronze
  - Quarter-sawn white oak
  - Honed Vermont marble
  - Bouclé linen
gallery:
  - src: /projects/coastal-monolith/gallery/01.svg
    alt: Bronze-clad fireplace anchoring the great room from floor to ceiling.
    caption: The fireplace — weathered bronze, structural, the only object in the great room that isn't plaster, wood, or built-in.
  - src: /projects/coastal-monolith/gallery/02.svg
    alt: Honed Vermont marble island over quarter-sawn white oak floors.
    caption: The marble island — oversized, unfussy, low-honed so it accepts the morning sun without glare.
  - src: /projects/coastal-monolith/gallery/03.svg
    alt: Linen sofa under a recessed display niche.
    caption: The sofa wall — bouclé linen, custom-made, sized to the room not to a catalog.
  - src: /projects/coastal-monolith/gallery/04.svg
    alt: Bronze sconce washing a limewash-plaster wall with warm light.
    caption: The reading nook — a bronze sconce with no visible switch. Light has architecture, not fixtures.
  - src: /projects/coastal-monolith/gallery/05.svg
    alt: View through an open doorway into the next room beyond.
    caption: From great room into study — a continuous gesture of plaster, threshold to threshold.
placeholder: true
---

The owners came with a single brief: *"We want a house that disappears."*

What they meant — we discovered through three months of conversation — was that they wanted a house that **didn't perform**. No grand entrance, no announced material moments, no rooms that demanded attention. They wanted the architecture to recede so the life inside it could come forward.

<PullQuote attribution="The brief, in one sentence">
We want a house that disappears.
</PullQuote>

The result is a long, low gesture of limewash plaster set into a coastal hammock. The great room is anchored by a single bronze-clad fireplace that runs floor to ceiling — the only object in the space that is not white, plaster, or wood. Everything else is built-in, soft, and quiet.

<Statline value="4,200 sq ft" label="Single-family residence — Cocoa Beach, FL" />

## Materials

The palette is small on purpose. Limewash plaster across every wall, ceiling included, hand-trowelled by the same artisan who finished a private chapel in Saint Augustine the year before. Quarter-sawn white oak floors, end-grain in the entry. A honed Vermont marble island, oversized and unfussy. And bronze — weathered, not polished — for everything that touches: door pulls, fireplace surround, fixtures.

## Light

We treated daylight as the primary lighting plan. Electric light is supplemental. Linear, indirect, and warm, hidden in coves and edges so the source is never the subject. The reading nook in the back of the great room is lit by a single bronze sconce — and the sconce has no switch you can see.

*Photography forthcoming. Project complete; archival images in development.*
